<script lang="ts" setup>
import type { StatementQueueItemRef  } from '@/types/view'
import type {StatementEvaluationResult } from '@/types/domain'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n';
import { inject } from 'vue'
import { DomainTransKey } from '@/i18n/domainTrans'
import { ColorScaleKey } from '@/ui/colorScaleContext'


const dt = inject(DomainTransKey)!
const {t} = useI18n();

const emit = defineEmits<{
  (e: 'save', payload: StatementEvaluationResult): void
  (e: 'cancel'): void
}>()

const props = defineProps<{
  statements: StatementQueueItemRef[]
}>()

const colorCtx = inject(ColorScaleKey)!


const currentItem = ref<StatementQueueItemRef>();
const items = ref<StatementQueueItemRef[]>([]);
const previousEvaluation = ref<number | null | undefined>(null);

onMounted(() => {
  items.value = props.statements;
  currentItem.value = props.statements.pop();
  if(currentItem.value){
    previousEvaluation.value = currentItem.value.statement.evaluation;

    if(currentItem.value.statement.evaluation == null || currentItem.value.statement.evaluation == undefined) {
      currentItem.value.statement.evaluation = 50;
    }

  }
})

const $text = computed(() => {

  if(currentItem.value == null ||  currentItem.value.statement.evaluation == null) {
    return t('likert.unanswered')
  }

  let computed = Math.round(((currentItem.value.statement.evaluation) / 10) / 2);

  switch(computed) {
    case 0:
      return t('likert.0')
    case 1:
      return t('likert.1')
    case 2:
      return t('likert.2')
    case 3:
      return t('likert.3')
    case 4:
      return t('likert.4')
    case 5:
      return t('likert.5')
    default:
      return t('likert.unanswered')
  }
})

const last = computed(() => {
  return items.value.length == 0;
})
const $color = computed(() => colorCtx.scale.value.at(currentItem.value?.statement.evaluation))

const onCancel = (() => {
  if(currentItem.value && previousEvaluation.value != null) {
    currentItem.value.statement.evaluation = previousEvaluation.value
  }

  emit('cancel');
})

const onSave = (() => {

  if(!currentItem.value) {
    return;
  }

  let evaluation = currentItem.value.statement.evaluation;
  if(evaluation == null || evaluation == undefined) {
    return;
  }

  let statementEvaluationResult: StatementEvaluationResult = {
    statementId: currentItem.value.statement.id,
    evaluation: evaluation
  }

  emit('save', statementEvaluationResult)

  if(items.value.length == 0) {
    emit('cancel');
  }

  currentItem.value = items.value.pop();
  if(currentItem.value) {
    previousEvaluation.value = currentItem.value.statement.evaluation;

    if(currentItem.value.statement.evaluation == null) {
      currentItem.value.statement.evaluation = 50;
    }
  }
});
</script>

<template>

  <div class="modal-container" @click.self="emit('cancel')" v-if="currentItem != null">
    <div class="modal-body">
      <span class="modal-close" @click="emit('cancel')">X</span>
      <h3>{{ dt.t(currentItem.topic.title, currentItem.topic.title_i18n) }} - {{ dt.t(currentItem.category.title, currentItem.category.title_i18n) }}</h3>
      <h3 class="header">{{ dt.t(currentItem.statement.title, currentItem.statement.title_i18n) }}</h3>
      <h3 :style="{'color': $color}">{{ $text }}</h3>
      <span>({{ currentItem.statement.evaluation }} / 100)</span>
      <div class="slider" >
        <vue3-slider v-model="currentItem.statement.evaluation" :color="$color" track-color="#FEFEFE"
              :alwaysShowHandle=true :handleScale=3 orientation="horizontal"
              width="60vw" :circleGap=270 :height=12 @keyup.enter="emit('save', {statementId: currentItem.statement.id, evaluation: currentItem.statement.evaluation || 50})" @keyup.esc="emit('cancel')"/>
      </div>
      <div class="modal-action">
        <button class="modal-button cancel" @click="onCancel()">{{ $t('common.close') }}</button>
        <template v-if="!last">
          <button class="modal-button continue" @click="onSave()">{{ $t('common.OK-next') }} ({{ items.length }} {{ $t('common.more-to-answer') }})</button>
        </template>
        <template v-if="last">
          <button class="modal-button continue" @click="onSave()">{{ $t('common.OK') }}</button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.flex-wrapper {
  display: flex;
  flex-direction: column;
}

.header {
  padding-top: 12px;
  padding-bottom: 40px;
  font-weight: bold;
}

.modal-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100vh;
    background-color: #2c3e50b4;
  }

.modal-body {
    color: #f0f0f0;
    background-color: #2c3e50;
    border-top: 2px solid #003a74;
    border-bottom: 2px solid #003a74;

    text-align: center;
    padding: 20px 40px;
    display: flex;
    flex-direction: column;
    width: 100%;
}

.slider {
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 40px 0;
}
.modal-action {
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-evenly;
}

button.modal-button {
  cursor: pointer;
  min-height: 45px;
}

.modal-close {
  cursor: pointer;
  position: relative;
  align-self: end;
  right: -33px;
  top: -17px;
}
</style>
